import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useLocalizeMessage, ModalWindow, Text, Select, Badge, RadioGroup, RadioGroupOption, Input, TextArea, Spinner, } from 'libs.nucleus';
import { useDashboards } from '../../hooks';
import { getStatusBadgeVariant, getStatusLabel } from '../../utils';
const getAvailableStatusOptions = (currentStatus, translate) => {
    if (currentStatus === 'DRAFT') {
        return [{ label: translate('Ready'), value: 'PUBLISHED' }];
    }
    if (currentStatus === 'PUBLISHED') {
        return [{ label: translate('In progress'), value: 'DRAFT' }];
    }
    return [];
};
export const ChangeStatusModal = ({ dashboard, onClose, onStatusChange }) => {
    const translate = useLocalizeMessage();
    const { transitionStatus, cloneDashboard, archiveDashboard, checkDashboardNameUnique, fetchDatasets, isLoading } = useDashboards();
    const [step, setStep] = useState('status');
    const [actionOption, setActionOption] = useState('CREATE_VERSION');
    // Clone form state
    const [cloneTitle, setCloneTitle] = useState('');
    const [cloneDescription, setCloneDescription] = useState('');
    const [cloneDatasetId, setCloneDatasetId] = useState('');
    const [cloneDatasetKey, setCloneDatasetKey] = useState('');
    const [cloneNameError, setCloneNameError] = useState('');
    const [isCloneNameValid, setIsCloneNameValid] = useState(false);
    const [isValidatingName, setIsValidatingName] = useState(false);
    const [datasets, setDatasets] = useState([]);
    const statusOptions = useMemo(() => getAvailableStatusOptions(dashboard.status, translate), [dashboard.status, translate]);
    const selectedStatus = statusOptions.length > 0 ? statusOptions[0].value : null;
    // Pre-select dataset from current dashboard
    useEffect(() => {
        const existingData = dashboard.data;
        const currentCubeConfigId = dashboard.cubeConfigId || existingData?.cubeConfigId || '';
        const currentCubeConfigKey = dashboard.cubeConfigKey || existingData?.cubeConfigKey || '';
        if (!cloneDatasetId && currentCubeConfigId) {
            setCloneDatasetId(currentCubeConfigId);
            setCloneDatasetKey(currentCubeConfigKey);
        }
    }, [dashboard, cloneDatasetId]);
    // Load datasets when entering clone form
    useEffect(() => {
        if (step === 'clone-form') {
            const loadDatasets = async () => {
                const result = await fetchDatasets();
                setDatasets(result);
            };
            loadDatasets();
        }
    }, [step, fetchDatasets]);
    const needsActionSelection = dashboard.status === 'PUBLISHED' && selectedStatus === 'DRAFT';
    const validateCloneName = useDebouncedCallback(async (nameValue) => {
        if (!nameValue.trim()) {
            setCloneNameError(translate('Name is required'));
            setIsValidatingName(false);
            setIsCloneNameValid(false);
            return;
        }
        setIsValidatingName(true);
        try {
            const isUnique = await checkDashboardNameUnique(nameValue, dashboard.id);
            if (!isUnique) {
                setCloneNameError(translate('A dashboard with this name already exists'));
                setIsCloneNameValid(false);
            }
            else {
                setCloneNameError('');
                setIsCloneNameValid(true);
            }
        }
        catch {
            setCloneNameError('');
            setIsCloneNameValid(true);
        }
        finally {
            setIsValidatingName(false);
        }
    }, 500);
    useEffect(() => {
        if (cloneTitle) {
            validateCloneName(cloneTitle);
        }
        else {
            setCloneNameError('');
            setIsCloneNameValid(false);
        }
    }, [cloneTitle, validateCloneName]);
    const handleConfirm = useCallback(async () => {
        if (!selectedStatus) {
            return;
        }
        // If going PUBLISHED -> DRAFT and action is CLONE, show clone form
        if (needsActionSelection && actionOption === 'CLONE' && step === 'status') {
            setCloneTitle(dashboard.name + ' (Copy)');
            setCloneDescription(dashboard.data?.description || '');
            setStep('clone-form');
            return;
        }
        try {
            if (needsActionSelection && actionOption === 'CREATE_VERSION' && step === 'status') {
                // Create version: clone with same key chain, then archive the original
                const newDashboard = await cloneDashboard(dashboard, 'CREATE_VERSION');
                await archiveDashboard(dashboard.id);
                onStatusChange(newDashboard);
            }
            else if (step === 'clone-form') {
                if (!cloneTitle.trim() || cloneNameError || !cloneDatasetId) {
                    return;
                }
                // Final uniqueness check before submitting
                setIsValidatingName(true);
                try {
                    const isUnique = await checkDashboardNameUnique(cloneTitle.trim());
                    if (!isUnique) {
                        setCloneNameError(translate('A dashboard with this name already exists'));
                        setIsValidatingName(false);
                        return;
                    }
                }
                catch {
                    // Allow to proceed if check fails
                }
                finally {
                    setIsValidatingName(false);
                }
                const newDashboard = await cloneDashboard(dashboard, 'CLONE', {
                    name: cloneTitle.trim(),
                    description: cloneDescription.trim(),
                    cubeConfigId: cloneDatasetId,
                    cubeConfigKey: cloneDatasetKey,
                });
                onStatusChange(newDashboard);
            }
            else {
                // Simple status transition
                const updated = await transitionStatus(dashboard.id, selectedStatus);
                onStatusChange(updated);
            }
            onClose();
        }
        catch {
            // Error handled in hook
        }
    }, [
        selectedStatus,
        needsActionSelection,
        actionOption,
        step,
        dashboard,
        cloneDashboard,
        archiveDashboard,
        checkDashboardNameUnique,
        cloneTitle,
        cloneDescription,
        cloneDatasetId,
        cloneDatasetKey,
        cloneNameError,
        transitionStatus,
        onStatusChange,
        onClose,
        translate,
    ]);
    const isConfirmDisabled = useMemo(() => {
        if (isLoading) {
            return true;
        }
        if (!selectedStatus) {
            return true;
        }
        if (step === 'clone-form') {
            return !cloneTitle.trim() || !!cloneNameError || !isCloneNameValid || isValidatingName || !cloneDatasetId;
        }
        return false;
    }, [isLoading, selectedStatus, step, cloneTitle, cloneNameError, isCloneNameValid, isValidatingName, cloneDatasetId]);
    const datasetOptions = useMemo(() => datasets.map((d) => ({ label: d.name, value: d.id })), [datasets]);
    const confirmButton = {
        label: step === 'clone-form' ? translate('Clone') : translate('Change status'),
        onClick: handleConfirm,
        disabled: isConfirmDisabled,
    };
    const footerButtons = step === 'clone-form'
        ? [
            {
                label: translate('Cancel'),
                onClick: onClose,
                disabled: isLoading,
            },
            {
                label: translate('Back'),
                onClick: () => setStep('status'),
                disabled: isLoading,
            },
        ]
        : [
            {
                label: translate('Cancel'),
                onClick: onClose,
                disabled: isLoading,
            },
        ];
    const getModalSubtitle = () => {
        if (step === 'clone-form') {
            return translate('Enter information for cloning.');
        }
        return undefined;
    };
    return (_jsxs(ModalWindow, { title: translate('Change status'), subtitle: getModalSubtitle(), isOpen: true, closeWindow: onClose, footerPrimaryActionButton: confirmButton, footerSecondaryActionButtons: footerButtons, children: [step === 'status' && (_jsxs("div", { className: 'space-y-6 p-4', children: [_jsxs("div", { className: 'space-y-1', children: [_jsx(Text, { className: 'text-text-secondary', children: translate('In progress asset can be edited.') }), _jsx(Text, { className: 'text-text-secondary', children: translate('Ready asset can be viewed by users without edit access.') })] }), _jsxs("div", { className: 'flex gap-12 items-center', children: [_jsx(Text, { children: translate('Current status') }), _jsx(Badge, { variant: getStatusBadgeVariant(dashboard.status), label: getStatusLabel(dashboard.status, translate) })] }), _jsxs("div", { className: 'flex gap-12 items-center', children: [_jsx(Text, { children: translate('New status') }), _jsx(Badge, { variant: getStatusBadgeVariant(selectedStatus), label: getStatusLabel(selectedStatus, translate) })] }), needsActionSelection && (_jsxs("div", { className: 'space-y-4', children: [_jsx(Text, { className: 'font-medium', children: translate('Action') }), _jsx(RadioGroup, { value: actionOption, onChange: (val) => setActionOption(val), children: _jsxs("div", { className: 'space-y-4', children: [_jsx("div", { className: `p-4 border border-solid rounded-lg cursor-pointer ${actionOption === 'CREATE_VERSION'
                                                ? 'bg-purple-50 border-purple-600 border-[1.5px]'
                                                : 'border-neutral-200 hover:bg-neutral-50'}`, onClick: () => setActionOption('CREATE_VERSION'), children: _jsx(RadioGroupOption, { option: {
                                                    value: 'CREATE_VERSION',
                                                    label: translate('Create new version'),
                                                    description: translate('Replace the current asset with a new version. Previous versions become archived and made read-only in the library.'),
                                                } }) }), _jsx("div", { className: `p-4 border border-solid rounded-lg cursor-pointer ${actionOption === 'CLONE'
                                                ? 'bg-purple-50 border-purple-600 border-[1.5px]'
                                                : 'border-neutral-200 hover:bg-neutral-50'}`, onClick: () => setActionOption('CLONE'), children: _jsx(RadioGroupOption, { option: {
                                                    value: 'CLONE',
                                                    label: translate('Clone asset'),
                                                    description: translate('Cloning an asset allows the existing one to remain available in the library while you create a new one. The status of the existing asset will not change.'),
                                                } }) })] }) })] }))] })), step === 'clone-form' && (_jsxs("div", { className: 'space-y-6 p-4', children: [_jsxs("div", { className: 'relative', children: [_jsx(Input, { id: 'clone-title', label: translate('Title'), value: cloneTitle, onChange: (e) => {
                                    setCloneTitle(e.target.value);
                                    setCloneNameError('');
                                }, width: 'full', required: true, hasError: !!cloneNameError, errorMessage: cloneNameError, autoComplete: 'off' }), isValidatingName && (_jsx("div", { className: 'absolute end-0 top-0', children: _jsx(Spinner, { size: 'sm' }) }))] }), _jsx(TextArea, { id: 'clone-description', label: translate('Description'), value: cloneDescription, onChange: (e) => setCloneDescription(e.target.value), width: 'full' }), _jsx(Select, { label: translate('Dataset'), required: true, options: datasetOptions, value: datasetOptions.find((o) => o.value === cloneDatasetId), onChange: (option) => {
                            const dataset = datasets.find((d) => d.id === option.value);
                            if (dataset) {
                                setCloneDatasetId(dataset.id);
                                setCloneDatasetKey(dataset.key);
                            }
                        }, width: 'full', disabled: datasets.length === 0 })] }))] }));
};
